import { groupSkuComponents, type SkuComponentLike } from '@/lib/skuComponents';

interface SkuComponentGroupsProps {
  components?: SkuComponentLike[];
  itemQuantity?: number;
  variant?: 'detail' | 'compact';
}

export default function SkuComponentGroups({
  components = [],
  itemQuantity = 1,
  variant = 'compact',
}: SkuComponentGroupsProps) {
  const { setItems, gifts } = groupSkuComponents(components);
  const groups = [
    {
      key: 'set-items',
      title: 'Состав набора',
      icon: 'ri-list-check-3',
      items: setItems,
      tone: 'amber',
    },
    {
      key: 'gifts',
      title: 'Подарки к заказу',
      icon: 'ri-gift-line',
      items: gifts,
      tone: 'honey',
    },
  ].filter((group) => group.items.length > 0);

  if (groups.length === 0) return null;

  if (variant === 'detail') {
    return (
      <div className="mt-6 grid gap-3">
        {groups.map((group) => (
          <section
            key={group.key}
            className={`rounded-3xl border p-4 ${
              group.tone === 'amber'
                ? 'border-amber/25 bg-amber/10'
                : 'border-honey/20 bg-card'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full ${
                group.tone === 'amber' ? 'bg-amber/20 text-dark' : 'bg-honey/10 text-honey-dark'
              }`}>
                <i className={group.icon} />
              </span>
              <h2 className="text-sm font-semibold text-dark">{group.title}</h2>
            </div>
            <div className="mt-3 divide-y divide-dark/5">
              {group.items.map((component) => (
                <div key={component.id} className="flex items-start justify-between gap-4 py-2 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-5 text-dark">{component.title}</p>
                    {component.comment ? (
                      <p className="mt-0.5 text-xs leading-5 text-dark/55">{component.comment}</p>
                    ) : null}
                  </div>
                  <span className="shrink-0 rounded-full bg-white/70 px-2.5 py-1 text-xs font-semibold text-dark/65">
                    × {component.quantity * itemQuantity}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {groups.map((group) => (
        <div key={group.key} className="rounded-xl bg-sand/35 px-2.5 py-2">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-dark/60">
            <i className={group.icon} />
            {group.title}
          </p>
          <div className="mt-1 space-y-1">
            {group.items.map((component) => (
              <div key={component.id} className="flex items-start justify-between gap-2 text-[11px] text-dark/50">
                <span className="min-w-0">
                  {component.title}
                  {component.comment ? <span className="block text-dark/40">{component.comment}</span> : null}
                </span>
                <span className="shrink-0">× {component.quantity * itemQuantity}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
